/**
 * Draw clearance zones and door swing arcs on the floor plan canvas.
 * Used during drag to show the user spatial constraints.
 */

import {
  getObjectClearanceCorners,
  getObjectClearanceFootprints,
} from '@shared-lib/collision/geometry';
import { getClearanceConfig } from '@shared-lib/collision/clearanceConfig';
import { calculateDoorZone } from '@shared-lib/collision/doorCollision';

// Cabinets have frontClearance: 0 in their config but still own per-sink
// clearance footprints derived from their sink children. Numeric
// frontClearance gates therefore need a cabinet escape hatch.
function _isCabinet(obj) {
  return (obj?.type || '').toLowerCase() === 'lower-cabinet';
}
function _hasMeaningfulClearance(obj, config) {
  if (_isCabinet(obj)) return true;
  return config.frontClearance > 0 || config.sideClearance > 0;
}

/**
 * Draw a polygon on the canvas.
 * @param {CanvasRenderingContext2D} ctx
 * @param {Object[]} corners - [{x, z}, ...]
 */
function drawPolygon(ctx, corners) {
  if (!corners || corners.length < 3) return;
  ctx.beginPath();
  ctx.moveTo(corners[0].x, corners[0].z);
  for (let i = 1; i < corners.length; i++) {
    ctx.lineTo(corners[i].x, corners[i].z);
  }
  ctx.closePath();
}

/**
 * Draw clearance zones and door arcs during object drag.
 *
 * @param {CanvasRenderingContext2D} ctx - Canvas context (already transformed to world space)
 * @param {Object} draggedObject - The object being dragged
 * @param {Object} roomData - { objects, doors, walls }
 * @param {number} scale - Current zoom scale
 */
export function drawDragClearanceZones(ctx, draggedObject, roomData, scale) {
  if (!draggedObject?.center || !draggedObject?.dimensions) return;

  const config = getClearanceConfig(draggedObject);
  const lineWidth = Math.max(1 / scale, 1.5 / scale);

  // 1. Draw dragged object's clearance zone(s).
  // Multi-footprint fixtures (cabinets) draw one polygon per sink.
  if (_hasMeaningfulClearance(draggedObject, config)) {
    const footprints = getObjectClearanceFootprints(
      draggedObject,
      config,
      roomData.objects || []
    );

    if (footprints.length > 0) {
      ctx.save();
      ctx.setLineDash([6 / scale, 4 / scale]);
      ctx.strokeStyle = 'rgba(59, 130, 246, 0.6)'; // Blue
      ctx.fillStyle = 'rgba(59, 130, 246, 0.08)';
      ctx.lineWidth = lineWidth;

      for (const fp of footprints) {
        drawPolygon(ctx, fp);
        ctx.fill();
        ctx.stroke();
      }

      // Label corner indices on the first footprint only — labels on every
      // sink rectangle would be visually noisy.
      const labelTarget = footprints[0];
      if (labelTarget?.length >= 4) {
        ctx.fillStyle = 'rgba(59, 130, 246, 0.9)';
        const labelSize = Math.max(0.06, 0.08 / scale);
        ctx.font = `bold ${labelSize}px monospace`;
        for (let i = 0; i < labelTarget.length; i++) {
          ctx.fillText(
            `${i}`,
            labelTarget[i].x + 0.02,
            labelTarget[i].z + 0.02
          );
        }
      }
      ctx.restore();
    }
  }

  // 2. Draw clearance zones of nearby objects that could be violated
  const objects = roomData.objects || [];
  for (const other of objects) {
    if (other.id === draggedObject.id) continue;
    if (
      draggedObject.fixtureGroupId &&
      other.fixtureGroupId === draggedObject.fixtureGroupId
    )
      continue;
    if (!other.center || !other.dimensions) continue;

    const otherConfig = getClearanceConfig(other);
    if (otherConfig.skipCollisionCheck) continue;
    if (!_hasMeaningfulClearance(other, otherConfig)) continue;

    // Only draw if the object is close enough to matter (within 2m)
    const dx = draggedObject.center.x - other.center.x;
    const dz = draggedObject.center.z - other.center.z;
    if (dx * dx + dz * dz > 4) continue; // 2m radius

    const otherFootprints = getObjectClearanceFootprints(
      other,
      otherConfig,
      objects
    );
    if (otherFootprints.length === 0) continue;

    ctx.save();
    ctx.setLineDash([4 / scale, 3 / scale]);
    ctx.strokeStyle = 'rgba(249, 115, 22, 0.5)'; // Orange
    ctx.fillStyle = 'rgba(249, 115, 22, 0.06)';
    ctx.lineWidth = lineWidth;

    for (const fp of otherFootprints) {
      drawPolygon(ctx, fp);
      ctx.fill();
      ctx.stroke();
    }
    ctx.restore();
  }

  // 3. Draw door swing arcs on the parent wall
  const doors = roomData.doors || [];
  const parentWallId = draggedObject.parentWallIds?.[0];

  for (const door of doors) {
    // Only show doors on the same wall or nearby
    const doorWallId = door.parentWallIds?.[0];
    if (parentWallId && doorWallId !== parentWallId) continue;

    const doorType = door.category?.door?.doorType || '';
    if (!doorType.toLowerCase().includes('inswing')) continue;

    const doorZones = calculateDoorZone(door, roomData.walls || []);
    if (!doorZones) continue;

    ctx.save();
    ctx.setLineDash([5 / scale, 3 / scale]);
    ctx.strokeStyle = 'rgba(239, 68, 68, 0.5)'; // Red
    ctx.fillStyle = 'rgba(239, 68, 68, 0.06)';
    ctx.lineWidth = lineWidth;

    for (const zone of doorZones) {
      drawPolygon(ctx, zone);
      ctx.fill();
      ctx.stroke();
    }
    ctx.restore();
  }
}

const CLEARANCE_HANDLE_SIZE = 8; // px
const CLEARANCE_HANDLE_HIT_SIZE = 16; // px — larger hit area

/**
 * Get clearance handle positions from clearance corners.
 * Returns handles on the midpoint of each edge:
 *   front: midpoint of front edge (corners 2-3)
 *   left:  midpoint of left edge (corners 3-0)
 *   right: midpoint of right edge (corners 1-2)
 */
function getClearanceHandlePositions(corners) {
  if (!corners || corners.length < 4) return null;
  return {
    front: {
      x: (corners[2].x + corners[3].x) / 2,
      z: (corners[2].z + corners[3].z) / 2,
    },
    left: {
      x: (corners[3].x + corners[0].x) / 2,
      z: (corners[3].z + corners[0].z) / 2,
    },
    right: {
      x: (corners[1].x + corners[2].x) / 2,
      z: (corners[1].z + corners[2].z) / 2,
    },
  };
}

/**
 * Draw clearance resize handles on the clearance zone.
 */
export function drawClearanceResizeHandles(ctx, obj, scale) {
  const config = getClearanceConfig(obj);
  if (!config.frontClearance && !config.sideClearance) return;

  const corners = getObjectClearanceCorners(obj, config, obj.clearanceOffset);
  const handles = getClearanceHandlePositions(corners);
  if (!handles) return;

  ctx.save();
  const handleSize = CLEARANCE_HANDLE_SIZE / scale;
  const half = handleSize / 2;

  Object.values(handles).forEach((pos) => {
    ctx.fillStyle = '#ffffff';
    ctx.strokeStyle = 'rgba(59, 130, 246, 0.9)';
    ctx.lineWidth = 1.5 / scale;

    // Diamond shape for clearance handles (to distinguish from square physical handles)
    ctx.beginPath();
    ctx.moveTo(pos.x, pos.z - half);
    ctx.lineTo(pos.x + half, pos.z);
    ctx.lineTo(pos.x, pos.z + half);
    ctx.lineTo(pos.x - half, pos.z);
    ctx.closePath();
    ctx.fill();
    ctx.stroke();
  });

  ctx.restore();
}

/**
 * Hit-test clearance handles. Returns 'front', 'left', 'right', or null.
 */
export function hitTestClearanceHandles(point, obj, scale) {
  const config = getClearanceConfig(obj);
  if (!config.frontClearance && !config.sideClearance) return null;

  const corners = getObjectClearanceCorners(obj, config, obj.clearanceOffset);
  const handles = getClearanceHandlePositions(corners);
  if (!handles) return null;

  const hitArea = CLEARANCE_HANDLE_HIT_SIZE / scale;
  const halfHit = hitArea / 2;

  for (const [type, pos] of Object.entries(handles)) {
    if (
      Math.abs(point.x - pos.x) < halfHit &&
      Math.abs(point.z - pos.z) < halfHit
    ) {
      return type;
    }
  }
  return null;
}
